import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { useGetActiveVersionsInfiniteQuery } from 'src/apiClients';
import type { ActiveVersionItem } from 'src/apiClients/queries';
import { NO_VERSION } from 'src/apiClients/queries/active-versions-query';
import {
    SortDirection,
    type DataTableCellValue,
    type DataTableColDef,
    type DataTableRow,
} from 'src/components/data-table/DataTable.types';
import { EntityListPage } from 'src/components/entity-list-page';
import { ROUTES } from 'src/utils/strings';

import { i18n } from './ActiveVersionsPage.i18n';
import { useStyles } from './ActiveVersionsPage.styles';

type DayRange = 30 | 60 | 90 | 180 | 'all';

const DAY_RANGE_OPTIONS: DayRange[] = [30, 60, 90, 180, 'all'];

const PRODUCT_DISPLAY_NAMES: Record<string, string> = {
    imagoadmin: 'Admin',
    imagoapi: 'API',
    imagocapture: 'Capture X',
    imagocmd: 'CMD',
    imagoconnect: 'Connect',
    imagomobile: 'Mobile',
    imagoportal: 'Portal',
    leapfrog: 'Leapfrog',
};

const PRODUCT_FILTER_OPTIONS = Object.entries(PRODUCT_DISPLAY_NAMES)
    .map(([value, label]) => ({ label, value }))
    .sort((a, b) => a.label.localeCompare(b.label));

const SEARCH_COLUMNS = ['version'];
const INITIAL_SORT = [{ field: 'latestAttempt', direction: SortDirection.DESC }];
const INITIAL_FILTERS = { product: ['imagoconnect', 'imagomobile', 'imagocapture'] };

const formatProductName = (value: DataTableCellValue): string => {
    if (!value || typeof value !== 'string') return '(Unknown)';
    return PRODUCT_DISPLAY_NAMES[value] ?? value;
};

const formatVersion = (value: DataTableCellValue): string => {
    if (!value || typeof value !== 'string') return '(No version)';
    return value;
};

const formatDateTime = (value: DataTableCellValue): string => {
    if (!value || typeof value !== 'string') return '';
    const parsed = dayjs(value);
    if (!parsed.isValid()) return String(value);
    return parsed.format('D MMM YYYY, hh:mm A');
};

const mapRow = (item: ActiveVersionItem): DataTableRow => ({
    id: item.id,
    product: item.product,
    version: item.version,
    totalCount: item.totalCount,
    latestAttempt: item.latestAttempt,
});

export const ActiveVersionsPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { classes } = useStyles();
    const [dayRange, setDayRange] = useState<DayRange>(30);

    const baseWhere = useMemo(() => {
        if (dayRange === 'all') return undefined;
        return { attemptedOn: { $gte: dayjs().subtract(dayRange, 'day').toISOString() } };
    }, [dayRange]);

    const titleText =
        dayRange === 'all'
            ? intl.formatMessage(i18n.allTime)
            : intl.formatMessage(i18n.lastNDays, { n: dayRange });

    const handleRangeChange = useCallback((_: MouseEvent, value: DayRange | null) => {
        if (value !== null) setDayRange(value);
    }, []);

    const columns = useMemo<DataTableColDef[]>(
        () => [
            {
                field: 'product',
                headerName: intl.formatMessage(i18n.product),
                width: 180,
                copyable: false,
                valueFormatter: formatProductName,
                filterOptions: PRODUCT_FILTER_OPTIONS,
            },
            {
                field: 'version',
                headerName: intl.formatMessage(i18n.version),
                flex: 1,
                minWidth: 150,
                copyable: false,
                valueFormatter: formatVersion,
            },
            {
                field: 'totalCount',
                headerName: intl.formatMessage(i18n.total),
                width: 120,
                copyable: false,
            },
            {
                field: 'latestAttempt',
                headerName: intl.formatMessage(i18n.latestSignIn),
                width: 220,
                copyable: false,
                valueFormatter: formatDateTime,
            },
        ],
        [intl],
    );

    const handleRowClick = useCallback(
        (row: DataTableRow) => {
            const product = String(row.product ?? '');
            const version = String(row.version ?? '');
            if (product) {
                const versionSegment = version || NO_VERSION;
                navigate(
                    `${ROUTES.ACTIVE_VERSIONS}/${encodeURIComponent(product)}/${encodeURIComponent(versionSegment)}`,
                );
            }
        },
        [navigate],
    );

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography className={classes.headerTitle}>{titleText}</Typography>
                <ToggleButtonGroup
                    value={dayRange}
                    exclusive
                    onChange={handleRangeChange}
                    size="small"
                >
                    {DAY_RANGE_OPTIONS.map((option) => (
                        <ToggleButton key={option} value={option}>
                            {option === 'all' ? 'All' : `${option}d`}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>
            <EntityListPage<ActiveVersionItem>
                key={dayRange}
                automationId="active-versions-page"
                autoHeight
                useQuery={useGetActiveVersionsInfiniteQuery}
                searchColumns={SEARCH_COLUMNS}
                initialSort={INITIAL_SORT}
                initialFilters={INITIAL_FILTERS}
                columns={columns}
                mapRow={mapRow}
                onRowClick={handleRowClick}
                baseWhere={baseWhere}
                titleWhenNoResults={intl.formatMessage(i18n.noVersionsTitle)}
                messageWhenNoResults={intl.formatMessage(i18n.noVersionsMessage)}
            />
        </Box>
    );
};
